import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCount } from '@/format';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  Navigator,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
  type NavNode,
} from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  SearchBar,
  errorMessage,
} from '@/components/ui';
import { AppIcon } from '@/components/AppIcon';
import { ReviewBanner, ReviewPill } from '@/components/Review';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { FarmHierarchyTree } from '@/screens/FarmHierarchyTree';
import { useAuth } from '@/stores/auth';
import { useShellNav } from '@/stores/shellNav';

type Farm = components['schemas']['FarmDto'];
type Shed = components['schemas']['ShedDto'];
type Section = components['schemas']['SectionDto'];
type Pen = components['schemas']['PenDto'];
type Store = components['schemas']['StoreDto'];
type HierarchyFarm = components['schemas']['HierarchyFarmDto'];
type RetireResult = components['schemas']['RetireResultDto'];

type Level = 'farm' | 'shed' | 'section' | 'pen';
type Notice = { tone: 'ok' | 'error'; message: string };

/**
 * What each level is called and where it lives in the API. Sheds, sections
 * and pens share the `mdm.shed` permissions — the server groups them that way.
 */
const LEVELS: Record<Level, { one: string; many: string; entity: string; path: string; perm: string }> = {
  farm: { one: 'farm', many: 'farms', entity: 'Farm', path: 'farms', perm: 'mdm.farm' },
  shed: { one: 'shed', many: 'sheds', entity: 'Shed', path: 'sheds', perm: 'mdm.shed' },
  section: { one: 'section', many: 'sections', entity: 'Section', path: 'sections', perm: 'mdm.shed' },
  pen: { one: 'pen', many: 'pens', entity: 'Pen', path: 'pens', perm: 'mdm.shed' },
};

const CHILD: Record<Level, Level | null> = { farm: 'shed', shed: 'section', section: 'pen', pen: null };

const NODE_KEY = 'poultry.farmManager.node';
const VIEW_KEY = 'poultry.farmManager.view';

/** One row of the grid, whatever level it belongs to. */
interface Row {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  isActive: boolean;
  level: Level;
  raw: Farm | Shed | Section | Pen;
}

/** Where a navigator node sits: its own record plus the names above it. */
interface Place {
  level: Exclude<Level, 'pen'>;
  id: string;
  code: string;
  nameEn: string;
  isActive: boolean;
  farm?: { id: string; code: string; nameEn: string };
  shed?: { id: string; code: string; nameEn: string };
}

function parseNode(node: string): { level: Exclude<Level, 'pen'>; id: string } | null {
  const [kind, id] = node.split(':');
  if (!id || (kind !== 'farm' && kind !== 'shed' && kind !== 'section')) return null;
  return { level: kind, id };
}

/** Every farm, shed and section keyed by its navigator id, with its parents. */
function indexPlaces(farms: HierarchyFarm[]): Map<string, Place> {
  const places = new Map<string, Place>();
  for (const f of farms) {
    const farm = { id: f.id, code: f.code, nameEn: f.nameEn };
    places.set(`farm:${f.id}`, { level: 'farm', ...farm, isActive: f.isActive });
    for (const s of f.sheds) {
      const shed = { id: s.id, code: s.code, nameEn: s.nameEn };
      places.set(`shed:${s.id}`, { level: 'shed', ...shed, isActive: s.isActive, farm });
      for (const se of s.sections) {
        places.set(`section:${se.id}`, {
          level: 'section',
          id: se.id,
          code: se.code,
          nameEn: se.nameEn,
          isActive: se.isActive,
          farm,
          shed,
        });
      }
    }
  }
  return places;
}

/** A name with its kind's icon in front, for grid cells. */
function Named({ kind, text }: { kind: Level; text: string }) {
  return (
    <span className="withicon">
      <AppIcon kind={kind} />
      {text}
    </span>
  );
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Why a farm name cannot be used, in the server's words for the same rule. */
function farmNameTakenMessage(farm: { code: string; nameEn: string; isActive: boolean }): string {
  return farm.isActive
    ? `${farm.code} is already named “${farm.nameEn}”. Use a different name.`
    : `${farm.code} “${farm.nameEn}” is deleted under this name. Reactivate it instead, or use a different name.`;
}

function retiredLabel(label: string, isActive: boolean): string {
  return isActive ? label : `${label} (deleted)`;
}

/** A farm or shed added by name alone elsewhere, waiting to be checked here. */
interface Pending {
  level: 'farm' | 'shed';
  id: string;
  code: string;
  nameEn: string;
  /** The farm itself, for a farm; the shed's farm, for a shed. */
  farmId: string;
}

/**
 * Everything waiting for review, farms before the sheds inside them: a farm
 * typed into the store form, a shed typed into the new flock form.
 */
function placesToReview(farms: HierarchyFarm[]): Pending[] {
  const pending: Pending[] = [];
  for (const f of farms) {
    if (f.isActive && f.needsReview) {
      pending.push({ level: 'farm', id: f.id, code: f.code, nameEn: f.nameEn, farmId: f.id });
    }
    for (const s of f.sheds) {
      if (s.isActive && s.needsReview) {
        pending.push({ level: 'shed', id: s.id, code: s.code, nameEn: s.nameEn, farmId: f.id });
      }
    }
  }
  return pending;
}

/** "1 farm", "2 sheds", "3 places" — what the tree's flag counts. */
function reviewCount(pending: Pending[]): string {
  const farms = pending.filter((p) => p.level === 'farm').length;
  const sheds = pending.length - farms;
  if (sheds === 0) return `${farms} ${farms === 1 ? 'farm' : 'farms'}`;
  if (farms === 0) return `${sheds} ${sheds === 1 ? 'shed' : 'sheds'}`;
  return `${pending.length} places`;
}

function buildNav(farms: HierarchyFarm[]): NavNode[] {
  const pending = placesToReview(farms);
  return [
    {
      id: 'all',
      label: 'All farms',
      icon: <AppIcon kind="allFarms" />,
      count: farms.length,
      flag: pending.length ? `${reviewCount(pending)} to review` : undefined,
      children: farms.map((f) => ({
        id: `farm:${f.id}`,
        label: retiredLabel(`${f.code} · ${f.nameEn}`, f.isActive),
        icon: <AppIcon kind="farm" />,
        flag: f.isActive && f.needsReview ? 'Needs review' : undefined,
        count: f.sheds.length,
        children: f.sheds.map((s) => ({
          id: `shed:${s.id}`,
          label: retiredLabel(`${s.code} · ${s.nameEn}`, s.isActive),
          icon: <AppIcon kind="shed" />,
          flag: s.isActive && s.needsReview ? 'Needs review' : undefined,
          count: s.sections.length,
          children: s.sections.map((se) => ({
            id: `section:${se.id}`,
            label: retiredLabel(`${se.code} · ${se.nameEn}`, se.isActive),
            icon: <AppIcon kind="section" />,
            count: se.pens.length,
          })),
        })),
      })),
    },
  ];
}

function toRow(level: Level, r: Farm | Shed | Section | Pen): Row {
  return { id: r.id, code: r.code, nameEn: r.nameEn, nameUr: r.nameUr, isActive: r.isActive, level, raw: r };
}

/** A farm or shed added by name alone elsewhere, not yet checked here. */
function awaitingReview(row: Row): boolean {
  if (!row.isActive) return false;
  if (row.level === 'farm') return (row.raw as Farm).needsReview;
  if (row.level === 'shed') return (row.raw as Shed).needsReview;
  return false;
}

/** Deleted, waiting for review, or active. Given `onReview` (the user may edit
 *  the place), a "Needs review" pill opens its review. */
function PlaceStatus({ row, onReview }: { row: Row; onReview?: (() => void) | undefined }) {
  if (!row.isActive) return <span className="pill pill--alert">⚠ Deleted</span>;
  if (awaitingReview(row)) return <ReviewPill label="Needs review" onReview={onReview} />;
  return <span className="pill">Active</span>;
}

/** Where a place waiting for review was typed in, in words. */
function addedFrom(level: 'farm' | 'shed'): string {
  return level === 'farm' ? 'the store form' : 'the flock form';
}

/** What the banner says about the farms and sheds waiting for review. */
function reviewMessage(pending: Pending[]): string {
  if (pending.length === 1) {
    const p = pending[0]!;
    return `${p.code} “${p.nameEn}” was added from ${addedFrom(p.level)} with just a name. Check its details and save to confirm.`;
  }
  const farms = pending.filter((p) => p.level === 'farm').length;
  const sheds = pending.length - farms;
  const parts = [
    farms ? `${farms} ${farms === 1 ? 'farm' : 'farms'}` : null,
    sheds ? `${sheds} ${sheds === 1 ? 'shed' : 'sheds'}` : null,
  ].filter(Boolean);
  return `${parts.join(' and ')} were added with just a name. Check their details and save to confirm.`;
}

function capacity(value: number | null): string {
  return formatCount(value);
}

const COLUMNS: Record<Level, Array<GridColumn<Row>>> = {
  farm: [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Farm', width: 220, render: (r) => <Named kind="farm" text={r.nameEn} /> },
    { key: 'district', header: 'District', width: 130, render: (r) => <span className="dim">{(r.raw as Farm).district ?? '—'}</span> },
    { key: 'manager', header: 'Manager', width: 150, render: (r) => <span className="dim">{(r.raw as Farm).managerName ?? '—'}</span> },
    { key: 'sheds', header: 'Sheds', align: 'end', width: 80, render: (r) => (r.raw as Farm).shedCount },
    { key: 'stores', header: 'Stores', align: 'end', width: 80, render: (r) => (r.raw as Farm).storeCount },
    { key: 'flocks', header: 'Flocks', align: 'end', width: 80, render: (r) => (r.raw as Farm).flockCount },
    { key: 'status', header: 'Status', width: 110, render: (r) => <PlaceStatus row={r} /> },
  ],
  shed: [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Shed', width: 220, render: (r) => <Named kind="shed" text={r.nameEn} /> },
    { key: 'capacity', header: 'Capacity', align: 'end', width: 110, render: (r) => capacity((r.raw as Shed).capacity) },
    {
      key: 'flock',
      header: 'Flock',
      width: 150,
      render: (r) => {
        const s = r.raw as Shed;
        return s.flockCode ? (
          <span className="withicon">
            <AppIcon kind="flock" size={14} />
            <span className="mono">{s.flockCode}</span> <span className="dim">day {s.flockAgeDays}</span>
          </span>
        ) : (
          <span className="dim">Empty</span>
        );
      },
    },
    {
      key: 'birds',
      header: 'Birds alive',
      align: 'end',
      width: 110,
      render: (r) => {
        const alive = (r.raw as Shed).birdsAlive;
        return alive === null ? <span className="dim">—</span> : formatCount(alive);
      },
    },
    { key: 'sections', header: 'Sections', align: 'end', width: 90, render: (r) => (r.raw as Shed).sectionCount },
    { key: 'status', header: 'Status', width: 110, render: (r) => <PlaceStatus row={r} /> },
  ],
  section: [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Section', width: 220, render: (r) => <Named kind="section" text={r.nameEn} /> },
    { key: 'pens', header: 'Pens', align: 'end', width: 80, render: (r) => (r.raw as Section).penCount },
    { key: 'status', header: 'Status', width: 110, render: (r) => <PlaceStatus row={r} /> },
  ],
  pen: [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Pen', width: 220, render: (r) => <Named kind="pen" text={r.nameEn} /> },
    { key: 'capacity', header: 'Capacity', align: 'end', width: 110, render: (r) => capacity((r.raw as Pen).capacity) },
    { key: 'status', header: 'Status', width: 110, render: (r) => <PlaceStatus row={r} /> },
  ],
};

const EMPTY: Record<Level, string> = {
  farm: 'No farms yet. Add one to get started.',
  shed: 'No sheds on this farm yet.',
  section: 'No sections — flocks can be placed on this shed directly.',
  pen: "No pens — flocks can be placed on this section's shed directly.",
};

/**
 * Farm Manager: every farm, shed, section and pen in one workbench.
 *
 * The tree on the left picks the place you are working in; the grid lists
 * what is inside it. Sections and pens are optional at every level — a shed
 * with none is a normal, permanent state, so nothing here pushes anyone to
 * create them.
 */
export function FarmManager() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [node, setNodeState] = useState<string>(() => readStored(NODE_KEY, 'all'));
  const [view, setViewState] = useState<'grid' | 'chart'>(() => readStored(VIEW_KEY, 'grid'));
  const [showRetired, setShowRetired] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<{ level: Level; mode: 'create' | 'edit' } | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  function setNode(next: string) {
    setNodeState(next);
    writeStored(NODE_KEY, next);
    setSelectedKey(null);
    setSearch('');
    setNotice(null);
  }

  function setView(next: 'grid' | 'chart') {
    setViewState(next);
    writeStored(VIEW_KEY, next);
  }

  const includeInactive = showRetired ? 'true' : undefined;

  const hierarchy = useQuery({
    queryKey: ['farms', 'hierarchy', showRetired],
    queryFn: () => api.get('/api/v1/farms/hierarchy', { includeInactive }),
  });
  const farmsTree = hierarchy.data ?? [];
  const places = indexPlaces(farmsTree);

  // A node that has since been deleted (with "Show deleted" off) or removed
  // falls back to All farms rather than showing an empty, unexplained grid.
  const parsed = parseNode(node);
  const place = parsed ? places.get(node) : undefined;
  const effectiveNode = parsed && hierarchy.isSuccess && !place ? 'all' : node;
  const current = effectiveNode === 'all' ? null : parseNode(effectiveNode);
  const level: Level = current ? CHILD[current.level]! : 'farm';
  const parentId = current?.id;
  const meta = LEVELS[level];

  const farmsList = useQuery({
    queryKey: ['farms', 'list', showRetired],
    queryFn: () => api.get('/api/v1/farms', { includeInactive }),
    enabled: level === 'farm',
  });
  const shedsList = useQuery({
    queryKey: ['sheds', parentId, showRetired],
    queryFn: () => api.get('/api/v1/sheds', { farmId: parentId, includeInactive }),
    enabled: level === 'shed' && !!parentId,
  });
  const sectionsList = useQuery({
    queryKey: ['sections', parentId, showRetired],
    queryFn: () => api.get('/api/v1/sections', { shedId: parentId, includeInactive }),
    enabled: level === 'section' && !!parentId,
  });
  const pensList = useQuery({
    queryKey: ['pens', parentId, showRetired],
    queryFn: () => api.get('/api/v1/pens', { sectionId: parentId, includeInactive }),
    enabled: level === 'pen' && !!parentId,
  });

  const list = { farm: farmsList, shed: shedsList, section: sectionsList, pen: pensList }[level];
  const allRows: Row[] = ((list.data ?? []) as Array<Farm | Shed | Section | Pen>).map((r) => toRow(level, r));
  const needle = search.trim().toLowerCase();
  const rows = needle
    ? allRows.filter((r) => r.code.toLowerCase().includes(needle) || r.nameEn.toLowerCase().includes(needle))
    : allRows;
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  function invalidate() {
    for (const key of ['farms', 'sheds', 'sections', 'pens', 'stores']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }

  const retire = useMutation({
    mutationFn: (row: Row) => api.delete<RetireResult>(`/api/v1/${LEVELS[row.level].path}/${row.id}`),
    onSuccess: (result) => {
      invalidate();
      setNotice({ tone: 'ok', message: result.message });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete it') }),
  });

  const reactivate = useMutation({
    mutationFn: (row: Row) => api.patch(`/api/v1/${LEVELS[row.level].path}/${row.id}`, { isActive: true }),
    onSuccess: (_, row) => {
      invalidate();
      setNotice({ tone: 'ok', message: `${row.code} is active again.` });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate it') }),
  });

  const parentRetired = place ? !place.isActive : false;

  // Farms added from the store form, and sheds added from the flock form,
  // wait here until someone checks them. The tree has every farm in scope,
  // whichever place is open, so the banner can follow the user anywhere in it.
  const canReviewLevel = (l: 'farm' | 'shed') => can(`${LEVELS[l].perm}.update`);
  const toReview = placesToReview(farmsTree);
  const insideFarmId = place ? (place.level === 'farm' ? place.id : place.farm?.id) : undefined;
  // Inside a farm that waits for review, or holding a shed that does, the
  // banner's Review opens that one.
  const nextToReview = toReview.find((p) => p.farmId === insideFarmId) ?? toReview[0];
  const canReview = nextToReview ? canReviewLevel(nextToReview.level) : false;

  /**
   * Opens a place's review from anywhere: to the list it lives in — the farm
   * list for a farm, its farm's sheds for a shed — and then its form.
   */
  function reviewPlace(p: Pick<Pending, 'level' | 'id' | 'farmId'>) {
    const node = p.level === 'farm' ? 'all' : `farm:${p.farmId}`;
    if (effectiveNode !== node) setNode(node);
    setSearch('');
    setSelectedKey(p.id);
    setTab('details');
    setForm({ level: p.level, mode: 'edit' });
  }

  const columns = COLUMNS[level].map((c) =>
    c.key === 'status'
      ? {
          ...c,
          render: (r: Row) => (
            <PlaceStatus
              row={r}
              onReview={
                r.level === 'farm' && canReviewLevel('farm')
                  ? () => reviewPlace({ level: 'farm', id: r.id, farmId: r.id })
                  : r.level === 'shed' && canReviewLevel('shed')
                    ? () => reviewPlace({ level: 'shed', id: r.id, farmId: (r.raw as Shed).farmId })
                    : undefined
              }
            />
          ),
        }
      : c,
  );

  function openRow(row: Row) {
    if (row.level !== 'pen') setNode(`${row.level}:${row.id}`);
  }

  function rowActions(row: Row): DesktopAction[] {
    const perm = LEVELS[row.level].perm;
    const child = CHILD[row.level];
    return [
      ...(child
        ? [{ label: `Open ${LEVELS[child].many}`, shortcut: 'Enter', onSelect: () => openRow(row) }]
        : []),
      {
        label: awaitingReview(row) ? 'Review' : 'Edit',
        onSelect: () => {
          setSelectedKey(row.id);
          setForm({ level: row.level, mode: 'edit' });
        },
        allowed: can(`${perm}.update`),
      },
      row.isActive
        ? {
            label: 'Delete',
            onSelect: () => {
              if (
                window.confirm(
                  `Delete ${row.code}? If it was never used, it is deleted. If it was used, it is switched off and its history stays.`,
                )
              ) {
                setSelectedKey(row.id);
                retire.mutate(row);
              }
            },
            allowed: can(`${perm}.delete`),
            danger: true,
            separatorBefore: true,
          }
        : {
            label: 'Reactivate',
            onSelect: () => {
              setSelectedKey(row.id);
              reactivate.mutate(row);
            },
            allowed: can(`${perm}.update`),
            separatorBefore: true,
          },
    ];
  }

  const addAction: DesktopAction = {
    label: `Add ${meta.one}`,
    primary: true,
    onSelect: () => setForm({ level, mode: 'create' }),
    allowed: can(`${meta.perm}.create`),
    enabled: !parentRetired,
    reason: `This ${current ? LEVELS[current.level].one : 'place'} is deleted. Reactivate it first.`,
  };

  const toolbarActions: DesktopAction[] = [
    addAction,
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Edit',
            onSelect: () => {},
            allowed: can(`${meta.perm}.update`),
            enabled: false,
            reason: `Choose a ${meta.one} first`,
            separatorBefore: true,
          },
        ]),
  ];

  const navigator = hierarchy.isError ? (
    <div className="stack">
      <ErrorNote message={hierarchy.error.message} />
    </div>
  ) : (
    <Navigator id="farm-manager" label="Farms" nodes={buildNav(farmsTree)} selectedId={effectiveNode} onSelect={setNode} />
  );

  // A form takes over the right-hand side; the tree stays, so you can see
  // where the thing you are adding will go. An edit whose row has since
  // dropped out of view (a search, a retire) simply closes.
  const activeForm = form && (form.mode === 'create' || selected) ? form : null;
  if (activeForm) {
    const editing = activeForm.mode === 'edit' && selected ? selected : undefined;
    const done = (message: string) => {
      invalidate();
      setNotice({ tone: 'ok', message });
      setForm(null);
    };
    const formNode = (
      <PlaceForm
        key={editing ? editing.id : `create-${effectiveNode}`}
        level={activeForm.level}
        initial={editing}
        parent={place}
        onCancel={() => setForm(null)}
        onSaved={done}
      />
    );
    return (
      <div className="workbench">
        <SplitView id="farm-manager" initial={260} firstLabel="farm list" first={navigator} second={<div className="workbench__main workbench__main--form">{formNode}</div>} />
      </div>
    );
  }

  const detailTabs: DetailTab[] = selected
    ? [
        { id: 'details', label: 'Details', content: <RowDetails row={selected} parent={place} /> },
        ...(selected.level === 'farm'
          ? [{ id: 'stores', label: 'Stores', content: <FarmStores farmId={selected.id} />, hidden: !can('inv.store.read') }]
          : []),
      ]
    : [];

  const retiredCount = allRows.filter((r) => !r.isActive).length;

  // The chart follows the tree: inside a farm it draws just that farm, since
  // a chart of every farm at once stops being readable past a handful.
  const chartFarmId = place ? (place.level === 'farm' ? place.id : place.farm?.id) : undefined;
  const chartFarms = chartFarmId ? farmsTree.filter((f) => f.id === chartFarmId) : farmsTree;

  const main = (
    <div className="workbench__main">
      <div className="filterbar">
        {view === 'grid' && (
          <SearchBar value={search} onChange={setSearch} placeholder={`Search ${meta.many} by code or name…`} />
        )}
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
        <div className="viewtoggle" role="tablist" aria-label="View">
          {(['grid', 'chart'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={`viewtoggle__btn${view === v ? ' viewtoggle__btn--on' : ''}`}
              onClick={() => setView(v)}
            >
              {v === 'grid' ? 'List' : 'Chart'}
            </button>
          ))}
        </div>
      </div>

      {view === 'grid' && <ActionBar actions={toolbarActions} />}

      {list.isError && <ErrorNote message={list.error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      {nextToReview && (
        <ReviewBanner
          message={reviewMessage(toReview)}
          onReview={canReview ? () => reviewPlace(nextToReview) : undefined}
        />
      )}

      {view === 'chart' ? (
        <div className="farmmanager__chart">
          <FarmHierarchyTree
            farms={chartFarms}
            onSelect={(id) => {
              setNode(id);
              setView('grid');
            }}
          />
        </div>
      ) : (
        <DataGrid
          id={`farm-manager-${level}`}
          label={meta.many}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onOpen={(row) => {
            setSelectedKey(row.id);
            if (row.level !== 'pen') openRow(row);
            else if (can(`${meta.perm}.update`)) setForm({ level: 'pen', mode: 'edit' });
          }}
          actions={rowActions}
          loading={list.isPending && list.fetchStatus !== 'idle'}
          empty={needle ? `No ${meta.many} match your search.` : EMPTY[level]}
        />
      )}
    </div>
  );

  return (
    <div className="workbench">
      <SplitView
        id="farm-manager"
        initial={260}
        firstLabel="farm list"
        first={navigator}
        second={
          view === 'chart' ? (
            main
          ) : (
            <SplitView
              id="farm-manager-detail"
              direction="column"
              initial={380}
              firstLabel={`${meta.many} list`}
              first={main}
              second={
                <DetailPane
                  tabs={detailTabs}
                  activeTab={tab}
                  onTabChange={setTab}
                  empty={`Choose a ${meta.one} to see its details.`}
                  title={
                    selected && (
                      <span>
                        <AppIcon kind={selected.level} /> <span className="mono">{selected.code}</span> {selected.nameEn}
                      </span>
                    )
                  }
                />
              }
            />
          )
        }
      />
      <StatusBar>
        <span className="withicon">
          <AppIcon kind={place ? place.level : 'allFarms'} size={14} />
          {place ? `${capitalise(LEVELS[place.level].one)}: ${place.code} · ${place.nameEn}` : 'All farms'}
        </span>
        <span>
          {rows.length} {rows.length === 1 ? meta.one : meta.many}
          {showRetired && retiredCount > 0 ? ` · ${retiredCount} deleted` : ''}
        </span>
        <span>{selected ? `Chosen: ${selected.code}` : `No ${meta.one} chosen`}</span>
      </StatusBar>
    </div>
  );
}

function RowDetails({ row, parent }: { row: Row; parent: Place | undefined }) {
  const items: Array<[string, ReactNode]> = [
    ['Code', <span key="code" className="mono">{row.code}</span>],
    ['Name', row.nameEn],
    ['Name (Urdu)', row.nameUr ? <span key="nameUr" className="urdu">{row.nameUr}</span> : '—'],
  ];

  if (row.level === 'farm') {
    const f = row.raw as Farm;
    items.push(
      ['District', f.district ?? '—'],
      ['Manager', f.managerName ?? '—'],
      ['Contact', f.contactNo ?? '—'],
      ['Sheds', f.shedCount],
      ['Stores', f.storeCount],
      ['Flocks', f.flockCount],
    );
  } else if (row.level === 'shed') {
    const s = row.raw as Shed;
    items.push(['Farm', `${s.farmCode} · ${s.farmName}`], ['Capacity', capacity(s.capacity)], ['Sections', s.sectionCount]);
    // A shed holds at most one live flock, so this is either that flock or
    // plainly empty — never a count someone has to interpret.
    items.push([
      'Flock',
      s.flockCode ? (
        <span key="flock" className="withicon">
          <AppIcon kind="flock" size={14} />
          <span className="mono">{s.flockCode}</span> {s.flockName} · day {s.flockAgeDays}
        </span>
      ) : (
        <span key="flock" className="dim">Empty</span>
      ),
    ]);
    if (s.birdsAlive !== null) items.push(['Birds alive', formatCount(s.birdsAlive)]);
  } else if (row.level === 'section') {
    const s = row.raw as Section;
    if (parent?.farm) items.push(['Farm', `${parent.farm.code} · ${parent.farm.nameEn}`]);
    items.push(['Shed', parent ? `${parent.code} · ${parent.nameEn}` : s.shedCode], ['Pens', s.penCount]);
  } else {
    const p = row.raw as Pen;
    if (parent?.farm) items.push(['Farm', `${parent.farm.code} · ${parent.farm.nameEn}`]);
    if (parent?.shed) items.push(['Shed', `${parent.shed.code} · ${parent.shed.nameEn}`]);
    items.push(['Section', parent ? `${parent.code} · ${parent.nameEn}` : p.sectionCode], ['Capacity', capacity(p.capacity)]);
  }

  items.push(['Status', <PlaceStatus key="status" row={row} />]);

  return (
    <dl className="detaillist">
      {items.map(([label, value]) => (
        <DetailRow key={label} label={label} value={value} />
      ))}
    </dl>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** The stores attached to a farm, read-only; stores are managed in Items Manager. */
function FarmStores({ farmId }: { farmId: string }) {
  const { can } = useAuth();
  const stores = useQuery({
    queryKey: ['stores', 'farm', farmId],
    queryFn: () => api.get('/api/v1/stores', { farmId, includeInactive: 'true' }),
  });

  if (stores.isPending) return <p className="muted">Loading…</p>;
  if (stores.isError) return <ErrorNote message={stores.error.message} />;

  const rows = stores.data as Store[];

  return (
    <div className="stack">
      {rows.length === 0 ? (
        <p className="muted">This farm has no store yet.</p>
      ) : (
        <dl className="detaillist">
          {rows.map((s) => (
            <DetailRow
              key={s.id}
              label={s.code}
              value={
                <span className="withicon">
                  <AppIcon kind="store" /> {s.nameEn} {!s.isActive && <span className="pill pill--alert">⚠ Deleted</span>}
                </span>
              }
            />
          ))}
        </dl>
      )}
      {can('inv.store.read') && (
        <div>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              writeStored('poultry.reference.category', 'stores');
              useShellNav.getState().navigate('inv-reference');
            }}
          >
            Manage stores in Items Manager
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Add or edit a farm, shed, section or pen.
 *
 * The code is never on the create form: the server assigns it, and showing a
 * box for something nobody chooses only invites the question "what should I
 * put here?". On edit it is shown read-only, because people quote codes to
 * each other and need to be able to read one off the record.
 *
 * Creating a farm also creates its store, and its first shed unless the box
 * is unticked — a farm with neither cannot receive stock or hold birds, so it
 * was never finished on its own.
 */
function PlaceForm({
  level,
  initial,
  parent,
  onCancel,
  onSaved,
}: {
  level: Level;
  initial?: Row | undefined;
  /** The place being added into (create), or the one the row belongs to (edit). */
  parent: Place | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const meta = LEVELS[level];
  const editing = initial !== undefined;

  const farm = level === 'farm' && initial ? (initial.raw as Farm) : undefined;
  const shed = level === 'shed' && initial ? (initial.raw as Shed) : undefined;
  // A farm added by name alone from the store form, or a shed added that way
  // from the flock form: saving here, even with nothing changed, is the check
  // that confirms it.
  const reviewing = farm?.needsReview === true || shed?.needsReview === true;
  const cap = initial && (level === 'shed' || level === 'pen') ? (initial.raw as Shed | Pen).capacity : null;

  const [form, setForm] = useState({
    nameEn: initial?.nameEn ?? '',
    nameUr: initial?.nameUr ?? '',
    district: farm?.district ?? '',
    managerName: farm?.managerName ?? '',
    contactNo: farm?.contactNo ?? '',
    capacity: cap === null ? '' : String(cap),
    // Only used when creating a farm.
    storeName: '',
    createShed: true,
    shedName: '',
    shedCapacity: '',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const hasCapacity = level === 'shed' || level === 'pen';
  const creatingFarm = !editing && level === 'farm';

  // No two farms may share a name, so ask the server once typing pauses. It
  // applies the same rule as on save: case, spacing and punctuation are
  // ignored, deleted farms count, and the farm being edited may keep its own.
  const typedName = form.nameEn.trim();
  const checkedName = useDebouncedValue(typedName);
  const nameCheck = useQuery({
    queryKey: ['farms', 'name-check', checkedName.toLowerCase(), initial?.id ?? null],
    queryFn: () =>
      api.get('/api/v1/farms/name-check', {
        name: checkedName,
        ...(initial ? { exceptId: initial.id } : {}),
      }),
    enabled: level === 'farm' && checkedName !== '',
    staleTime: 0,
  });
  const nameTaken =
    level === 'farm' && checkedName === typedName && nameCheck.isSuccess ? nameCheck.data.clash : undefined;
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const common: Record<string, unknown> = { nameEn: form.nameEn.trim() };

      if (editing) {
        // Blank optional fields are sent blank so they clear.
        common.nameUr = form.nameUr.trim();
        if (level === 'farm') {
          common.district = form.district.trim();
          common.managerName = form.managerName.trim();
          common.contactNo = form.contactNo.trim();
        }
        // Saving is the review, whichever form was typed into.
        if (reviewing) common.needsReview = false;
        if (hasCapacity) common.capacity = form.capacity.trim() ? Number(form.capacity) : null;
        await api.patch(`/api/v1/${meta.path}/${initial.id}`, common);
        return initial.code;
      }

      if (form.nameUr.trim()) common.nameUr = form.nameUr.trim();
      if (level === 'farm') {
        if (form.district.trim()) common.district = form.district.trim();
        if (form.managerName.trim()) common.managerName = form.managerName.trim();
        if (form.contactNo.trim()) common.contactNo = form.contactNo.trim();
      }
      if (hasCapacity && form.capacity.trim()) common.capacity = Number(form.capacity);
      if (creatingFarm) {
        if (form.storeName.trim()) common.storeName = form.storeName.trim();
        common.createShed = form.createShed;
        if (form.createShed) {
          if (form.shedName.trim()) common.shedName = form.shedName.trim();
          if (form.shedCapacity.trim()) common.shedCapacity = Number(form.shedCapacity);
        }
      }
      if (level === 'shed') common.farmId = parent!.id;
      if (level === 'section') common.shedId = parent!.id;
      if (level === 'pen') common.sectionId = parent!.id;

      const created = await api.post<{
        id: string;
        code: string;
        store?: { code: string; nameEn: string };
        shed?: { code: string; nameEn: string } | null;
      }>(`/api/v1/${meta.path}`, common);

      // Creating a farm makes three things; the message says so, since the
      // store and shed are not what the person typed.
      const alsoMade = [
        created.store ? `store ${created.store.nameEn}` : null,
        created.shed ? `shed ${created.shed.nameEn}` : null,
      ].filter(Boolean);

      return alsoMade.length
        ? `${created.code} added, with its ${alsoMade.join(' and ')}.`
        : `${created.code} added.`;
    },
    onSuccess: (message) =>
      onSaved(editing ? (reviewing ? `${message} checked and confirmed.` : `${message} updated.`) : message),
  });

  const parentLabel = parent ? `${parent.code} · ${parent.nameEn}` : '';
  const parentWord = parent ? LEVELS[parent.level].one : '';
  const title = editing ? `Edit ${initial.code}` : `Add a ${meta.one}`;
  const intro = editing
    ? undefined
    : level === 'farm'
      ? 'Its code is given automatically. A store is created with it, and its first shed unless you say otherwise.'
      : `It will be added to ${parentWord} ${parentLabel}. Its code is given automatically.`;

  return (
    <FormPanel title={title} intro={intro} icon={<AppIcon kind={level} size={18} />}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          const issue = nameTaken ? farmNameTakenMessage(nameTaken) : null;
          setProblem(issue);
          if (!issue) save.mutate();
        }}
      >
        {reviewing && (
          <ReviewBanner
            message={
              level === 'farm'
                ? 'Added from the store form with just a name. Check its name, Urdu name, district, manager and contact, then save to confirm.'
                : 'Added from the flock form with just a name. Check its name, Urdu name and capacity, then save to confirm.'
            }
          />
        )}
        <FormGroup title="Basic details" columns={3} card>
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field
            label="Name"
            required
            value={form.nameEn}
            onChange={(nameEn) => set({ nameEn })}
            invalid={!!nameTaken}
            hint={nameTaken ? <span style={{ color: 'var(--c-alert)' }}>{farmNameTakenMessage(nameTaken)}</span> : undefined}
          />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => set({ nameUr })} />
          {level !== 'farm' && parent && (
            <Field
              label={capitalise(parentWord)}
              value={parentLabel}
              onChange={() => {}}
              disabled
            />
          )}
          {hasCapacity && (
            <Field
              label="Capacity"
              type="number"
              min={0}
              value={form.capacity}
              onChange={(capacity) => set({ capacity })}
              hint="Birds it can hold. For information only."
            />
          )}
        </FormGroup>

        {level === 'farm' && (
          <FormGroup title="Location & contact" columns={3} card>
            <Field label="District" value={form.district} onChange={(district) => set({ district })} placeholder="Okara" />
            <Field label="Manager" value={form.managerName} onChange={(managerName) => set({ managerName })} />
            <Field label="Contact" value={form.contactNo} onChange={(contactNo) => set({ contactNo })} placeholder="0300-1234567" />
          </FormGroup>
        )}

        {creatingFarm && (
          <FormGroup
            title="What comes with it"
            hint="Stock is received into a store, and birds are placed in a shed. Both can be renamed or added to later."
            columns={3}
            card
          >
            <Field
              label="Store name"
              value={form.storeName}
              onChange={(storeName) => set({ storeName })}
              placeholder={form.nameEn.trim() ? `${form.nameEn.trim()} Store` : 'Farm name + Store'}
              hint="Left blank, it is named after the farm"
            />
            <label className="settings__toggle formgrid__full">
              <input
                type="checkbox"
                checked={form.createShed}
                onChange={(e) => set({ createShed: e.target.checked })}
              />
              <span className="field__label">Create the first shed too</span>
            </label>
            {form.createShed && (
              <>
                <Field
                  label="Shed name"
                  value={form.shedName}
                  onChange={(shedName) => set({ shedName })}
                  placeholder="Shed 1"
                  hint="Left blank, it is called Shed 1"
                />
                <Field
                  label="Shed capacity"
                  type="number"
                  min={0}
                  value={form.shedCapacity}
                  onChange={(shedCapacity) => set({ shedCapacity })}
                  hint="Birds it can hold. For information only."
                />
              </>
            )}
          </FormGroup>
        )}

        {problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : reviewing ? 'Save and confirm' : editing ? 'Save changes' : `Add ${meta.one}`}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
